import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface BankNameComboboxProps {
  value: string;
  suggestion: string;
  onChange: (value: string) => void;
  id?: string;
  /**
   * When true the IBAN resolved to a known registry bank, so the name is the
   * authoritative registry value and is shown read-only. Free-text entry stays
   * available only on a miss, so an off-list bank can still be typed in.
   */
  locked?: boolean;
  /** Extra classes for the input (e.g. an error border). */
  className?: string;
}

// Typeable combobox for the bank name. The dropdown offers the bank derived
// from the IBAN as a one-click suggestion, while the input stays free-text so
// a bank that isn't in the dataset can still be entered manually.
export function BankNameCombobox({
  value,
  suggestion,
  onChange,
  id = 'bank_name',
  locked = false,
  className,
}: BankNameComboboxProps) {
  const [open, setOpen] = useState(false);

  // Matched a registry bank: the name comes from the admin-managed registry,
  // so lock it rather than let it drift into a typo'd variant.
  if (locked) {
    return (
      <Input
        id={id}
        value={value}
        readOnly
        className={cn('cursor-not-allowed', className)}
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Input
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="e.g. ING Bank N.V."
            className={cn('pr-9', className)}
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Show bank suggestion"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-(--radix-popover-trigger-width) p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {suggestion ? (
          <button
            type="button"
            onClick={() => {
              onChange(suggestion);
              setOpen(false);
            }}
            className={cn(
              'flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-accent',
              value === suggestion && 'font-medium'
            )}
          >
            <span className="truncate">{suggestion}</span>
            {value === suggestion && (
              <Check className="h-4 w-4 shrink-0 text-success" />
            )}
          </button>
        ) : (
          <p className="px-2 py-1.5 text-sm text-gray-500">
            Enter a valid IBAN to suggest the matching bank, or type the bank
            name.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
